/**
 * arbi ask <question> [-b] [-n] [-c <msg-id>] [-w <id>] [-q]
 *
 * Default: Streams SSE response from POST /v1/assistant/query.
 * -b/--background: Submits via POST /v1/responses with background=true,
 *   returns immediately with a task ID. Use `arbi task` to check status.
 * -c/--continue: Continue from a specific message ID (e.g. a completed
 *   background task result). Overrides the automatic chat session.
 *
 * Always includes all workspace documents in retrieval context.
 * Maintains conversation continuity: each response's assistant message ID
 * is saved and sent as previous_response_id in the next query.
 * Use --new to start a fresh conversation.
 *
 * Session handling: The chat session stores the workspace ID alongside the
 * conversation state. When the resolved workspace differs from the stored
 * session workspace (either because the user switched workspaces or passed
 * a different -w flag), the stale session is cleared and a fresh
 * conversation starts automatically.
 */

import { Command } from 'commander'
import {
  streamSSE,
  formatAgentStepLabel,
  formatStreamSummary,
  countCitations,
  stripCitationMarkdown,
  documents,
  assistant,
  responses,
} from '@arbidocs/sdk'
import { runAction, resolveWorkspace } from '../helpers.js'
import {
  getConfig,
  getChatSession,
  updateChatSession,
  clearChatSession,
  saveLastMetadata,
} from '../config.js'
import { addTask } from '../task-store.js'
import { chalk } from '../output.js'

export function registerAskCommand(program: Command): void {
  program
    .command('ask <question...>')
    .description('Ask the RAG assistant a question (no quotes needed)')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('-c, --continue <msg-id>', 'Continue from a specific message ID')
    .option('--config <id>', 'Config ext_id to use (e.g. cfg-xxx)')
    .option('-n, --new', 'Start a new conversation (ignore previous context)')
    .option('-b, --background', 'Submit as background task — fetch result with `arbi task result`')
    .option('-q, --quiet', 'Suppress agent steps and tool calls')
    .option('--json', 'Output in JSON format (background mode only)')
    .option(
      '--raw-citations',
      'Keep raw `[text](#cite-N)` markdown links instead of the cleaner `text[N]` form'
    )
    .action(
      (
        words: string[],
        opts: {
          workspace?: string
          continue?: string
          config?: string
          new?: boolean
          background?: boolean
          quiet?: boolean
          json?: boolean
          rawCitations?: boolean
        }
      ) =>
        runAction(async () => {
          // `--json` is meaningful only on the background path — the streaming
          // SSE response is prose, not JSON. Silently ignoring `--json`
          // (the old behavior) means an agent expecting structured output
          // gets back a transcript and no signal anything went wrong. Fail
          // loudly so the caller knows to add `-b`.
          if (opts.json && !opts.background) {
            console.error(
              'Error: --json only works with -b/--background. Streaming mode emits prose, not JSON.\nRun: arbi ask -b --json <question>  (then `arbi task result`)'
            )
            process.exit(1)
          }

          const question = words.join(' ')
          const { arbi, accessToken, workspaceId, config } = await resolveWorkspace(opts.workspace)

          // Resolve previousResponseId from flags and session
          let previousResponseId: string | null = null

          if (opts.continue) {
            // Explicit message ID — overrides session
            previousResponseId = opts.continue
          } else if (opts.new) {
            clearChatSession()
          } else {
            const session = getChatSession()
            const workspaceChanged =
              session.lastMessageExtId && session.workspaceId && session.workspaceId !== workspaceId

            if (workspaceChanged) {
              clearChatSession()
            } else if (session.lastMessageExtId) {
              previousResponseId = session.lastMessageExtId
            }
          }

          // Always include all workspace documents in retrieval context
          const docs = await documents.listDocuments(arbi)
          const docIds = docs.map((d) => d.external_id as string)

          // doc_ext_id → file name, so agent steps can show "Reading
          // Contract A.pdf" instead of the opaque "doc-xxxxxxxx" id.
          const docNames: Record<string, string> = {}
          for (const d of docs) {
            if (d.external_id && d.file_name) docNames[d.external_id as string] = d.file_name
          }

          // ── Background mode ──────────────────────────────────────────────
          // Background tasks start a standalone conversation (no parent ID).
          // Use `arbi ask --continue <msg-id>` to follow up on a result.
          if (opts.background) {
            const result = await responses.submitBackgroundQuery({
              baseUrl: config.baseUrl,
              accessToken,
              workspaceId,
              question,
              docIds,
              previousResponseId: null,
              model: opts.config,
            })

            // Store locally for `arbi task list/status/result`
            addTask({
              id: result.id,
              question: question.slice(0, 200),
              workspaceId,
              submittedAt: new Date().toISOString(),
              status: 'queued',
              model: result.model,
            })

            if (opts.json) {
              console.log(JSON.stringify({ id: result.id, status: result.status }))
            } else {
              console.log(`Task queued: ${chalk.cyan(result.id)}`)
              console.log(
                chalk.dim('Use `arbi task status` or `arbi task result` to check progress.')
              )
            }
            return
          }

          // ── Streaming mode (default) ─────────────────────────────────────

          // Query the assistant (retry as new conversation if parent message is stale)
          let res: Response
          try {
            res = await assistant.queryAssistant({
              baseUrl: config.baseUrl,
              accessToken,
              workspaceId,
              question,
              docIds,
              previousResponseId,
              model: opts.config,
            })
          } catch (err) {
            // Detect stale parent message via HTTP 404 or specific error text.
            // This handles edge cases like conversation deletion on the server.
            const isStaleParent =
              previousResponseId &&
              err instanceof Error &&
              (err.message.includes('404') || err.message.includes('Parent message not found'))

            if (!isStaleParent) throw err

            // Parent message no longer exists — start a new conversation
            clearChatSession()
            res = await assistant.queryAssistant({
              baseUrl: config.baseUrl,
              accessToken,
              workspaceId,
              question,
              docIds,
              previousResponseId: null,
              model: opts.config,
            })
          }

          // Resolve verbosity: flag > config > default (true)
          const verbose = opts.quiet === true ? false : getConfig()?.verbose !== false

          let elapsedTime: number | null = null
          let firstToken = true

          // The backend re-emits some lifecycle steps at
          // the same `index` — once at the start, once on completion to
          // attach token cost. The web merges same-index events into one
          // row; the linear CLI can't, so we suppress a re-emission that
          // produces the same label at the same index. Distinct steps (e.g.
          // two searches) have different indices and still print.
          let lastStepIndex: number | null = null
          let lastStepLabel = ''

          // The backend embeds citations inline as `[claim text](#cite-N)`
          // markdown links inside the streamed tokens. Default behavior: pipe
          // tokens through `stripCitationMarkdown` so a human (and an agent
          // grepping for `\[\d+\]`) sees a clean `claim text[1]` form. Pass
          // `--raw-citations` to keep the original markdown — useful when
          // piping to a markdown renderer that resolves the anchors itself.
          //
          // Streaming complication: a citation marker can be split across
          // token chunks. We buffer the tail until it's safe to flush.
          let buf = ''
          const transform = opts.rawCitations ? (s: string) => s : stripCitationMarkdown
          const flush = (final: boolean) => {
            // If we might be mid-marker, hold the tail. The longest possible
            // partial-marker prefix is `](#cite-` (8 chars) — keep at least
            // 32 chars buffered to cover the inner text too.
            const cut = final ? buf.length : Math.max(0, buf.length - 32)
            if (cut === 0) return
            const out = transform(buf.slice(0, cut))
            buf = buf.slice(cut)
            if (out) process.stdout.write(out)
          }

          const result = await streamSSE(res, {
            onToken: (content) => {
              if (firstToken) {
                process.stderr.write(chalk.dim('[ARBI] '))
                firstToken = false
              }
              buf += content
              flush(false)
            },
            onAgentStep: (data) => {
              if (verbose) {
                const label = formatAgentStepLabel(data, docNames)
                // Skip "Writing answer" — the [agent] prefix is printed inline with the first token
                if (label && data.step !== 'answering') {
                  // Suppress same-index re-emissions of the same label (the
                  // start/finish duplicate — the same step sent twice).
                  const isDuplicate = data.index === lastStepIndex && label === lastStepLabel
                  if (!isDuplicate) {
                    console.error(chalk.dim(`\n[ARBI] ${label}`))
                    lastStepIndex = data.index
                    lastStepLabel = label
                  }
                }
              }
            },
            onElapsedTime: (t) => {
              elapsedTime = t
            },
            onError: (message) => console.error(chalk.red(`\nError: ${message}`)),
          })

          // Flush any remainder still in the citation-aware buffer.
          flush(true)
          process.stdout.write('\n')

          // Persist metadata for `arbi cite`
          if (result.metadata) {
            saveLastMetadata(result.metadata)
          }

          // Print summary line (right-aligned, hidden only in quiet mode)
          const refs = countCitations(result.metadata)
          const summary = opts.quiet !== true ? formatStreamSummary(result, elapsedTime) : ''
          if (summary) {
            const refSuffix = refs > 0 ? ` · ${refs} ref${refs === 1 ? '' : 's'}` : ''
            const text = `[${summary}${refSuffix}]`
            const cols = process.stderr.columns || 80
            const pad = Math.max(0, cols - text.length)
            console.error(chalk.dim(' '.repeat(pad) + text))
          }

          // Print citation hint
          if (refs > 0 && opts.quiet !== true) {
            console.error(
              chalk.dim(`[${refs} citation${refs === 1 ? '' : 's'} — arbi cite to browse]`)
            )
          }

          // Save conversation state for continuity and history restoration.
          // Always save the resolved workspaceId so future calls can detect changes.
          //
          // The SDK's `streamSSE` only captures `assistantMessageExtId` from
          // `response.created` events; some backend configs don't emit that
          // event yet. The result metadata still has the assistant message's
          // `external_id`, so we fall back to it — otherwise every `arbi ask`
          // would start a new conversation because the session pointer never
          // updates.
          const assistantId =
            result.assistantMessageExtId ?? (result.metadata?.external_id as string | undefined)
          if (assistantId) {
            const updates: Partial<{
              lastMessageExtId: string
              conversationExtId: string
              workspaceId: string
            }> = {
              lastMessageExtId: assistantId,
              workspaceId,
            }
            const conversationExtId =
              result.userMessage?.conversation_ext_id ?? result.metadata?.conversation_ext_id
            if (conversationExtId) {
              updates.conversationExtId = conversationExtId
            }
            updateChatSession(updates)
          } else if (opts.quiet !== true) {
            // No assistant message ID anywhere → the response didn't emit the
            // events the SDK needs (`response.created` / `arbi.message_metadata`
            // / `response.completed`). Probably truncated mid-stream by an
            // upstream agent failure. Warn loudly so the caller knows the
            // next `arbi ask` will start a fresh conversation instead of
            // resuming this one — an agent script that chains multiple asks
            // would otherwise silently fan out into N orphan conversations.
            console.error(
              chalk.yellow(
                '[warn] No assistant message ID received — session not saved. ' +
                  'The next `arbi ask` will start a new conversation.'
              )
            )
          }
        })()
    )
}
